import moment from "moment-timezone";
import Decimal from "decimal.js";
import { Badge } from "@/components/ui/badge";
import type {
  ExpandedTransformedEntry,
  ExpandedTransformedSettlement,
  ExpandedTransformedAdvance,
  ExpandedGrade,
  ExpandedChargeGradeDetail,
  TransformedSplit,
} from "@/lib/api/agtech/ticketsExpanded";

// ── Helpers ────────────────────────────────────────────────────────

export function fmt(val: string | null | undefined): string {
  if (val === null || val === undefined || val === "") return "—";
  return val;
}

export function fmtNum(val: string | null | undefined, conversion_factor?: string | null): string {
  if (val === null || val === undefined) return "—";

  return new Decimal(val).div(conversion_factor ?? 1).toNumber().toLocaleString()
}

export function fmtWeight(val: string | null | undefined, uom = " lbs"): string {
  if (val === null || val === undefined) return "—";
  return Number(val).toLocaleString() + uom;
}

// ── Field ──────────────────────────────────────────────────────────

export function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] font-medium text-muted-foreground leading-tight">{label}</span>
      <span className="text-xs leading-tight">{value}</span>
    </div>
  );
}

export function NumericField({
  label,
  value,
  uom,
  conversion_factor,
}: {
  label: string;
  value: string | null | undefined;
  uom: string;
  conversion_factor?: string | null;
}) {
  const formatted =
    value === null || value === undefined
      ? null
      : new Decimal(value).div(conversion_factor ?? 1).toNumber().toLocaleString();

  return (
    <div className="flex flex-col">
      <span className="text-[10px] font-medium text-muted-foreground leading-tight">{label}</span>
      {formatted === null ? (
        <span className="text-xs leading-tight">—</span>
      ) : (
        <span className="text-xs leading-tight">
          <span className="font-medium">{formatted}</span>
          <span className="text-[10px] text-muted-foreground ml-0.5">{uom}</span>
        </span>
      )}
    </div>
  );
}

// ── Grades sub-block ───────────────────────────────────────────────

export function GradesBlock({ grades }: { grades: ExpandedGrade[] }) {
  if (grades.length === 0) return null;

  return (
    <div className="mt-1">
      <span className="text-[10px] font-medium text-muted-foreground">Grades</span>
      <div className="grid grid-cols-4 gap-x-3 gap-y-0.5 mt-0.5">
        {grades.map((g, i) => (
          <div key={`${g.charge_grade_code}-${i}`} className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{g.charge_grade_code}</span>
            <span className="font-medium">{fmt(g.charge_grade_value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Base grades sub-block (origin / destination) ───────────────────

export function BaseGradesBlock({ grades }: { grades: ExpandedChargeGradeDetail[] }) {
  if (grades.length === 0) return null;

  return (
    <div className="mt-1">
      <span className="text-[10px] font-medium text-muted-foreground">Grades</span>
      <div className="grid grid-cols-4 gap-x-3 gap-y-0.5 mt-0.5">
        {grades.filter((g) => g.charge_grade_value_o !== '0').map((g, i) => (
          <div key={`${g.charge_grade_code}-${i}`} className="text-xs">
            <span className="text-muted-foreground">{g.charge_grade_code}:</span>
            <span className="ml-1 font-medium">{fmt(g.charge_grade_value_o)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Settlement block ───────────────────────────────────────────────

export function SettlementBlock({ s, e, onClick }: { s: ExpandedTransformedSettlement; e: ExpandedTransformedEntry; onClick?: () => void }) {
  return (
    <div className={`rounded border bg-background p-2 space-y-1${onClick ? " cursor-pointer hover:bg-muted/50 transition-colors" : ""}`} onClick={onClick}>
      <div className="flex items-center gap-2">
        <Badge variant="default" className="text-[10px] px-1.5 py-0">Settlement</Badge>
        <span className="text-xs font-semibold">#{(s.serial_nbr !== null ? String(s.serial_nbr) : undefined) ?? "-"}</span>
      </div>
      <div className="grid grid-cols-4 gap-x-4 gap-y-1">
        <Field label="Contract" value={String(s.contract?.contract_id ?? "-")} />
        <Field label="Contract Type" value={String(s.contract?.pricing_type_code ?? "-")} />
        {s.contract?.quantity && (<NumericField label="Contract Qty" value={String(s.contract?.quantity ?? "")} uom={""} />)}
        <NumericField label="VP Qty" value={s.vp_quantity} uom={e.dest_uom} />
        <NumericField label="VP Applied" value={s.vp_applied_quantity} uom={e.dest_uom} />
        {s.ShipFrom?.long_name !== e.ShipFrom?.long_name && (<Field label="Ship From" value={s.ShipFrom?.long_name ?? fmt(s.ship_from_id)} />)}
        {s.ShipTo?.long_name !== e.ShipTo?.long_name && (<Field label="Ship To" value={s.ShipTo?.long_name ?? fmt(s.ship_to_id)} />)}
      </div>
    </div>
  );
  // <GradesBlock grades={s.grades} />
}

// ── Advance block ──────────────────────────────────────────────────

export function AdvanceBlock({ a, e, onClick }: { a: ExpandedTransformedAdvance; e: ExpandedTransformedEntry; onClick?: () => void }) {
  return (
    <div className={`rounded border bg-background p-2 space-y-1${onClick ? " cursor-pointer hover:bg-muted/50 transition-colors" : ""}`} onClick={onClick}>
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Advance</Badge>
        <span className="text-xs font-semibold">#{(a.serial_nbr !== null ? String(a.serial_nbr) : undefined) ?? "-"}</span>
      </div>
      <div className="grid grid-cols-4 gap-x-4 gap-y-1">
        <Field label="Contract" value={String(a.contract?.contract_id ?? "-")} />
        <Field label="Contract Type" value={String(a.contract?.pricing_type_code ?? "-")} />
        <NumericField label="VP Qty" value={a.vp_quantity} uom={e.dest_uom} />
        <NumericField label="VP Applied" value={a.vp_applied_quantity} uom={e.dest_uom} />
        {a.ShipFrom?.long_name !== e.ShipFrom?.long_name && (<Field label="Ship From" value={a.ShipFrom?.long_name ?? fmt(a.ship_from_id)} />)}
        {a.ShipTo?.long_name !== e.ShipTo?.long_name && (<Field label="Ship To" value={a.ShipTo?.long_name ?? fmt(a.ship_to_id)} />)}
      </div>
    </div>
  );
  // <GradesBlock grades={a.grades} />
}

// ── Splits block ───────────────────────────────────────────────────

export function SplitsBlock({ splits, e }: { splits: TransformedSplit[]; e: ExpandedTransformedEntry }) {
  if (splits.length === 0) return null;

  return (
    <div className="space-y-1">
      <div className="grid grid-cols-1 gap-1">
        {splits.map((sp, i) => (
          <div key={i} className="rounded border bg-background p-2 grid grid-cols-5 gap-x-4 gap-y-1">
            <Field label="Related" value={(sp.TicketRelation?.long_name ?? "")} />
            <Field label="Split Percentage" value={`${new Decimal(sp.split_percentage ?? 1).times(100)}%`} />
            <NumericField label="Split" value={sp.scale_quantity_lb} uom={e.dest_uom} conversion_factor={e.commodity_conversion_factor} />
            <NumericField label="Applied" value={sp.applied_quantity_lb} uom={e.dest_uom} conversion_factor={e.commodity_conversion_factor} />
            <NumericField label="Shrink" value={sp.shrink_quantity_lb} uom={e.dest_uom} conversion_factor={e.commodity_conversion_factor} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Entry block ────────────────────────────────────────────────────

export function EntryBlock({ entry, onContractClick }: { entry: ExpandedTransformedEntry; onContractClick?: (contractNbr: number) => void }) {
  const netWeightRaw =
    entry.scale_ticket_gross_weight !== null && entry.scale_ticket_tare_weight !== null
      ? String(Number(entry.scale_ticket_gross_weight) - Number(entry.scale_ticket_tare_weight))
      : null;

  return (
    <div className="rounded-lg border bg-muted/50 p-3 space-y-2">
      {/* Entry header */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold">Entry #{entry.quantity_entry_nbr}</span>
      </div>

      <div className="grid grid-cols-4 gap-x-4 gap-y-1">
        <Field label="Origin" value={entry.ShipFrom?.long_name ?? fmt(entry.ship_from_id)} />
        <Field label="Destination" value={entry.ShipTo?.long_name ?? fmt(entry.ship_to_id)} />
        <Field label="Vehicle" value={fmt(entry.vehicle_id)} />
        <Field label="Reference" value={fmt(entry.reference_code)} />
      </div>

      {/* Weights row */}
      <div className="grid grid-cols-4 gap-x-4 gap-y-1">
        <NumericField label="Gross" value={entry.scale_ticket_gross_weight} uom="lbs" />
        <NumericField label="Tare" value={entry.scale_ticket_tare_weight} uom="lbs" />
        <NumericField label="Net" value={netWeightRaw} uom="lbs" />
        <NumericField label="Scale" value={entry.scale_quantity_lb} uom={entry.dest_uom} conversion_factor={entry.commodity_conversion_factor} />
      </div>

      {/* Base Grades */}
      <BaseGradesBlock grades={entry.grades} />

      {/* Splits */}
      <SplitsBlock splits={entry.splits} e={entry}/>

      {/* Settlements */}
      {entry.settlements.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-xs font-semibold">
            Settlements ({entry.settlements.length})
          </h4>
          <div className="grid grid-cols-1 gap-1">
            {entry.settlements.map((s) => (
              <SettlementBlock
                key={s.settlement_nbr}
                s={s}
                e={entry}
                onClick={s.contract_nbr && onContractClick ? () => onContractClick(s.contract_nbr) : undefined}
              />
            ))}
          </div>
        </div>
      )}

      {/* Advances */}
      {entry.advances.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-xs font-semibold">
            Advances ({entry.advances.length})
          </h4>
          <div className="grid grid-cols-1 gap-1">
            {entry.advances.map((a) => (
              <AdvanceBlock
                key={a.advance_nbr}
                a={a}
                e={entry}
                onClick={a.contract_nbr && onContractClick ? () => onContractClick(a.contract_nbr) : undefined}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
